import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { endOfDay, format, sub } from 'date-fns';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { LinkTrackingDto } from '@/Domain/api/Analytics/dto/LinkTracking.dto';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { DateRangeState } from '@/Domain/types/DateRange.types';

// Formats a Date as YYYY-MM-DD in the current locale without shifting the timezone
const toISODate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

export const useLinkTracking = (externalRange?: DateRangeState) => {
  const queryClient = useQueryClient();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const defaultEnd = endOfDay(new Date());
  const defaultStart = endOfDay(sub(defaultEnd, { days: 6 }));

  const [internalRange, setInternalRange] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
  });

  const range = externalRange || internalRange;
  const startDate = range.startDate;
  const endDate = range.endDate;
  const start = toISODate(startDate);
  const end = toISODate(endDate);
  const rangeKey = `${start}:${end}`;
  const [paginationRangeKey, setPaginationRangeKey] = useState(rangeKey);
  const rangeChanged = paginationRangeKey !== rangeKey;
  const visiblePagination = rangeChanged ? { ...pagination, pageIndex: 0 } : pagination;

  useEffect(() => {
    if (!rangeChanged) return;
    setPagination((old) => (old.pageIndex === 0 ? old : { ...old, pageIndex: 0 }));
    setPaginationRangeKey(rangeKey);
  }, [rangeChanged, rangeKey]);

  const handleSortingChange: Dispatch<SetStateAction<SortingState>> = (updater) => {
    setPagination((old) => (old.pageIndex === 0 ? old : { ...old, pageIndex: 0 }));
    setSorting(updater);
  };

  const pageIndex = visiblePagination.pageIndex;
  const sortKey = sorting[0] ? sorting[0].id : undefined;
  const sortDesc = sorting[0] ? sorting[0].desc : undefined;
  const { data, isLoading, isError, isPlaceholderData } = useQuery<LinkTrackingDto>({
    queryKey: [QueryKey.analytics.linkTracking, { start, end, pageIndex, sortKey, sortDesc }],
    placeholderData: keepPreviousData,
  });

  const { data: widgetSettings } = useQuery<WidgetSettingsDto>({
    queryKey: [QueryKey.widgetSettings],
  });

  const mutation = useMutation<WidgetSettingsDto, unknown, Partial<WidgetSettingsDto>>({
    mutationKey: [QueryKey.widgetSettings],
    mutationFn: async (updatedData) => {
      queryClient.setQueryData([QueryKey.widgetSettings], (prev: WidgetSettingsDto) => ({
        ...prev,
        ...updatedData,
      }));

      return await httpClient
        .patch('/v1/api/widget/configuration', updatedData)
        .then((res) => res.data);
    },
    onError: (_, __, context) => {
      queryClient.setQueryData([QueryKey.widgetSettings], () => context);
    },
    // Toggling tracking changes what the table should show, so don't wait out staleTime.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.analytics.linkTracking] });
    },
  });

  const handleToggleLinkTracking = (check: boolean) =>
    mutation.mutate({ link_tracking_active: check });

  const handleRangeChange = (dateRange: DateRange | undefined) => {
    if (!externalRange && dateRange?.from && dateRange?.to) {
      setInternalRange({
        startDate: dateRange.from,
        endDate: dateRange.to,
      });
    }
  };

  return {
    data,
    isLoading,
    isError,
    isPlaceholderData,
    handleRangeChange,
    handleToggleLinkTracking,
    range,
    sorting,
    pagination: visiblePagination,
    setPagination,
    setSorting: handleSortingChange,
    isLinkTrackingActive: widgetSettings?.link_tracking_active,
  };
};
